import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import { useActiveBundle } from '@/context/active-bundle';
import { localSearchAtom, type LocalSearchResult } from './local-search';
import { sentryCaptureException } from '@/utils/sentry';

export interface LocalSearchResultGroup {
  heading: string;
  results: LocalSearchResult[];
}

/**
 * Returns local search results from registered search handlers.
 * Only calls handlers when query is non-empty. Results are grouped by heading.
 * Each handler call is wrapped in try/catch so one bad handler doesn't break others.
 */
export function useLocalSearchResults(query: string): LocalSearchResultGroup[] {
  const searchMap = useStore(localSearchAtom);
  const activeApp = useActiveBundle();

  return useMemo(() => {
    if (query.length === 0) {
      return [];
    }

    const groupMap = new Map<string, LocalSearchResult[]>();

    for (const [, entry] of searchMap) {
      if (activeApp !== entry.app) {
        continue;
      }

      let results: LocalSearchResult[];
      try {
        results = entry.handler(query);
        if (!Array.isArray(results) || results.length === 0) {
          continue;
        }
      } catch (error) {
        sentryCaptureException(
          error instanceof Error ? error : new Error(`Local search handler "${entry.providerId}" failed`),
        );
        continue;
      }

      const existing = groupMap.get(entry.heading);
      if (existing != null) {
        existing.push(...results);
      } else {
        groupMap.set(entry.heading, [...results]);
      }
    }

    return [...groupMap.entries()].map(([heading, results]) => ({ heading, results }));
  }, [searchMap, activeApp, query]);
}
